import { useEffect, useState } from 'react';

/**
 * True while the page is scrolled past `fraction` (0–1) of its total
 * scrollable height. Gates the detail pages' island CTAs (the project's
 * Book-a-Meeting claim, the product's enquiry fallback and the nav claims'
 * handover) so they only engage once the reader is well into the page.
 *
 * Direction-symmetric with hysteresis: engages at `fraction`, releases only
 * below `fraction - releaseMargin` — scroll jitter at the exact boundary
 * (trackpad momentum, zoom-rounded scroll positions) can therefore never
 * thrash the consumers, which re-register island claims on each flip.
 * SSR-safe (starts false, measures on mount).
 */
export const useScrolledPast = (
    fraction: number,
    releaseMargin = 0.05,
): boolean => {
    const [past, setPast] = useState(false);

    useEffect(() => {
        const measure = () => {
            const doc = document.documentElement;
            const max = doc.scrollHeight - doc.clientHeight;
            const progress = max > 0 ? window.scrollY / max : 0;
            setPast((current) =>
                current
                    ? progress >= fraction - releaseMargin
                    : progress >= fraction,
            );
        };
        measure();
        window.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
        };
    }, [fraction, releaseMargin]);

    return past;
};
